import { REFERENCE } from './reference.js'

function nonempty(value) {
	return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function matchVariant(product, ref) {
	const variants = Array.isArray(product?.variants) ? product.variants : []
	const byId = variants.find((variant) => Number(variant?.bcId) === Number(ref.bcVariantId))
	if (byId) return byId
	if (ref.sku) {
		const bySku = variants.find((variant) => String(variant?.sku) === String(ref.sku))
		if (bySku) return bySku
	}
	return null
}

export function productImage(product, variant) {
	const fromVariant = nonempty(variant?.imageUrl)
	if (fromVariant) return fromVariant
	const images = Array.isArray(product?.images) ? product.images : []
	const thumb = images.find((image) => image?.isThumbnail) || images[0]
	return nonempty(thumb?.url) || nonempty(thumb?.thumbnail) || null
}

export function sellablePrice(product, variant, fallback) {
	const candidates = [variant?.currentPrice, variant?.calculatedPrice, variant?.salePrice, variant?.price, product?.currentPrice, product?.calculatedPrice, product?.salePrice, product?.price]
	for (const candidate of candidates) {
		const number = typeof candidate === 'string' && candidate.trim() !== '' ? Number(candidate) : candidate
		if (typeof number === 'number' && Number.isFinite(number)) return number
	}
	return fallback
}

// Same sold-out rule as the product page Add to Bag button
// (product.vue isAddToBagDisabled) and products.outOfStock:
// availability other than "available", or inventory tracked on the product
// with inventoryLevel <= 0, or inventory tracked per variant with that
// variant's inventoryLevel <= 0. inventoryTracking "none" ignores a zero
// count. purchasingDisabled matches the option picker, which will not offer
// that variant.
export function isSellableOutOfStock(product, variant) {
	if (!product) return null
	if (variant?.purchasingDisabled) return true
	const tracking = product.inventoryTracking
	const productOut = product.outOfStock === true || product.availability != 'available' || (tracking === 'product' && product.inventoryLevel <= 0)
	if (productOut) return true
	if (tracking === 'variant') {
		if (!variant) return true
		return variant.inventoryLevel <= 0
	}
	return false
}

export function maxQty(sellable) {
	const product = sellable?.product
	if (!product) return 99
	if (product.inventoryTracking === 'variant') {
		const level = Number(sellable.variant?.inventoryLevel)
		return Number.isFinite(level) ? Math.max(0, level) : 0
	}
	if (product.inventoryTracking === 'product') {
		const level = Number(product.inventoryLevel)
		return Number.isFinite(level) ? Math.max(0, level) : 0
	}
	return 99
}

function resolveSellable(ref, byId, loaded, issues, { useLiveName }) {
	const product = byId.get(Number(ref.bcProductId)) || null
	const variant = product ? matchVariant(product, ref) : null
	if (loaded && !product) {
		issues.push({ bcProductId: ref.bcProductId, bcVariantId: ref.bcVariantId, sku: ref.sku, reason: 'product-not-found' })
	} else if (loaded && product && !variant) {
		issues.push({ bcProductId: ref.bcProductId, bcVariantId: ref.bcVariantId, sku: ref.sku, reason: 'variant-not-found' })
	}
	const stock = product ? isSellableOutOfStock(product, variant) : null
	const matched = !!(product && variant)
	return {
		...ref,
		name: useLiveName && nonempty(product?.name) ? product.name : ref.name,
		sku: nonempty(variant?.sku) || ref.sku,
		price: matched ? sellablePrice(product, variant, ref.price) : ref.price,
		image: productImage(product, variant) || ref.image,
		outOfStock: matched ? !!stock : product ? true : !!ref.fallbackOutOfStock,
		product: matched ? product : null,
		variant: matched ? variant : null,
		bcVariantId: variant?.bcId != null ? Number(variant.bcId) : ref.bcVariantId,
	}
}

export function mergeCatalog(reference = REFERENCE, products = [], meta = {}) {
	const loaded = !!meta.loaded
	const list = Array.isArray(products) ? products : []
	const byId = new Map()
	for (const product of list) {
		if (product && product.bcId != null) byId.set(Number(product.bcId), product)
	}
	const issues = []
	const liners = {}
	for (const [key, liner] of Object.entries(reference.liners)) {
		liners[key] = resolveSellable(liner, byId, loaded, issues, { useLiveName: true })
	}
	const pools = reference.pools.map((pool) => ({
		...pool,
		variants: pool.variants.map((variant) => resolveSellable(variant, byId, loaded, issues, { useLiveName: false })),
	}))
	const accessories = reference.accessories.map((group) => ({
		group: group.group,
		items: group.items.map((item) => {
			if (item.options) {
				return {
					...item,
					options: item.options.map((option) => resolveSellable({ ...option, name: item.name, image: item.image }, byId, loaded, issues, { useLiveName: false })),
				}
			}
			return resolveSellable(item, byId, loaded, issues, { useLiveName: false })
		}),
	}))
	return {
		liners,
		pools,
		accessories,
		loaded,
		issues,
	}
}
